import type { AgentApiCandidateFormItemsShow } from '@/types/serializers';
import { LuExternalLink } from 'react-icons/lu';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import Select from '@/components/Select';
import { Radio } from '@/components/Radio';
import Checkbox from '@/components/Checkbox';
import RequiredLabel from '@/components/auth/RequiredLabel';
import { FileUploadDropzone } from '@/features/portal/components/FileUploadDropzone';

interface Props {
  formItem?: AgentApiCandidateFormItemsShow;
  questionPageUrl?: string;
}

function PreviewHeader({ questionPageUrl }: { questionPageUrl?: string }) {
  return (
    <div className="mb-xs flex items-center justify-between">
      <label className="text-heading-xxs text-main">プレビュー</label>
      <Button asChild color="tertiary" size="md">
        <a href={questionPageUrl || '#'} target="_blank" rel="noopener noreferrer">
          質問ページプレビュー
          <LuExternalLink className="size-4" />
        </a>
      </Button>
    </div>
  );
}

export default function Preview({ formItem, questionPageUrl }: Props) {
  if (!formItem) {
    return (
      <div className="mt-xl w-full">
        <PreviewHeader questionPageUrl={questionPageUrl} />
        <div className="w-full rounded-sm bg-white p-lg shadow-[0px_0px_3px_0px_rgba(0,0,0,0.2)]">
          <p className="text-body-sm text-sub">項目を選択してください</p>
        </div>
      </div>
    );
  }

  const selectOptions = Array.isArray(formItem.select_options)
    ? (formItem.select_options as string[])
    : [];

  // フォーマットタイプに応じたinput typeとpatternを取得
  const getInputProps = () => {
    const baseProps: { type?: string; pattern?: string; minLength?: number; maxLength?: number } = {
      minLength: formItem.min_length || undefined,
      maxLength: formItem.max_length || undefined,
    };

    switch (formItem.format_type) {
      case 'email':
        return { ...baseProps, type: 'email' };
      case 'tel':
      case 'tel_hyphen':
        return { ...baseProps, type: 'tel' };
      case 'url':
        return { ...baseProps, type: 'url' };
      case 'number':
        return { ...baseProps, type: 'number' };
      case 'hiragana':
        return { ...baseProps, type: 'text', pattern: '[ぁ-んー　、。\\s]*' };
      case 'katakana':
        return { ...baseProps, type: 'text', pattern: '[ァ-ヶー　ｦ-ﾝﾞﾟ\\s]*' };
      default:
        return { ...baseProps, type: 'text' };
    }
  };

  const renderInput = () => {
    switch (formItem.form_type) {
      case 'text':
        return (
          <div className="flex items-center gap-2">
            <Input
              {...getInputProps()}
              className="mt-1 w-[500px] flex-1"
              placeholder={formItem.placeholder || ''}
            />
            {formItem.suffix && (
              <span className="shrink-0 text-sm text-gray-700">{formItem.suffix}</span>
            )}
          </div>
        );

      case 'textarea':
        return (
          <Textarea className="mt-1 w-full" placeholder={formItem.placeholder || ''} rows={5} />
        );

      case 'selectbox':
        return (
          <Select
            items={
              selectOptions.length > 0
                ? selectOptions.map((option) => ({ value: option, label: option }))
                : [{ value: '', label: '選択してください' }]
            }
            defaultLabel={formItem.placeholder || '選択してください'}
            className="mt-1 w-full"
          />
        );

      case 'radio':
        return (
          <div className="mt-1 flex flex-col gap-2">
            {selectOptions.length > 0 ? (
              selectOptions.map((option, index) => (
                <Radio
                  key={index}
                  type="radio"
                  name={`preview-radio-${formItem.id}`}
                  value={option}
                >
                  {option}
                </Radio>
              ))
            ) : (
              <p className="text-sm text-gray-600">選択肢が設定されていません</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="mt-1 flex flex-col gap-2">
            {selectOptions.length > 0 ? (
              selectOptions.map((option, index) => <Checkbox key={index}>{option}</Checkbox>)
            ) : (
              <p className="text-sm text-gray-600">選択肢が設定されていません</p>
            )}
          </div>
        );

      case 'date': {
        // 年のオプション（現在の年+10年から1900年まで逆順）
        const currentYear = new Date().getFullYear();
        const yearOptions = Array.from({ length: currentYear - 1900 + 11 }, (_, i) => {
          const year = currentYear + 1 - i;
          return { value: String(year), label: String(year) };
        });

        // 月のオプション（1-12）
        const monthOptions = Array.from({ length: 12 }, (_, i) => {
          const month = i + 1;
          return { value: String(month), label: String(month) };
        });

        // 日のオプション（1-31）
        const dayOptions = Array.from({ length: 31 }, (_, i) => {
          const day = i + 1;
          return { value: String(day), label: String(day) };
        });

        return (
          <div className="mt-1 flex gap-4">
            <div className="flex items-center gap-1">
              <div className="w-[100px]">
                <Select items={yearOptions} defaultLabel="-" className="w-full" />
              </div>
              <span className="text-sm text-gray-700">年</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-[80px]">
                <Select items={monthOptions} defaultLabel="-" className="w-full" />
              </div>
              <span className="text-sm text-gray-700">月</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-[80px]">
                <Select items={dayOptions} defaultLabel="-" className="w-full" />
              </div>
              <span className="text-sm text-gray-700">日</span>
            </div>
          </div>
        );
      }

      case 'file':
        return (
          <div className="mt-1">
            <FileUploadDropzone
              onFileSelect={() => {}}
              validateFileType={() => true}
              className="mt-0"
            />
          </div>
        );

      default:
        return (
          <Input type="text" className="mt-1 w-full" placeholder={formItem.placeholder || ''} />
        );
    }
  };

  return (
    <div className="mt-xl w-full">
      <PreviewHeader questionPageUrl={questionPageUrl} />
      <div className="w-full rounded-sm bg-white p-lg shadow-[0px_0px_3px_0px_rgba(0,0,0,0.2)]">
        <div className="w-full">
          <div className="mb-xs flex items-center gap-xs">
            <label className="text-heading-xxs text-main">{formItem.title || ''}</label>
            {formItem.required && <RequiredLabel />}
          </div>
          {formItem.description && (
            <p className="mb-xs text-body-sm text-sub">{formItem.description}</p>
          )}
          <div className="w-full">{renderInput()}</div>
        </div>
      </div>
    </div>
  );
}
